import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import {
  HubPage, Container, Disclaimer, CommunityCTA, ResourceCard, TypeChip, BetaCTA, Band, HeroMedia, NewsletterSignup, Seo,
} from "../components/hub/HubKit";
import {
  resourceBySlug, categoryBySlug, needsDisclaimer, relatedResources, typeLabel,
} from "../data/hub";
import { track } from "../lib/analytics";

const Prose = ({ heading, accent, children }) => (
  <section className="mt-10">
    <div className="flex items-center gap-3">
      <span className="h-5 w-1 rounded-full" style={{ backgroundColor: accent }} />
      <h2 className="serif-display text-[1.5rem] sm:text-[1.8rem] font-normal text-charcoal">{heading}</h2>
    </div>
    <div className="mt-3 space-y-3 pl-4 text-base leading-relaxed text-ink/80">{children}</div>
  </section>
);

const Bullets = ({ items, accent }) => (
  <ul className="space-y-2.5">
    {items.map((i) => (
      <li key={i} className="flex gap-3 text-base leading-relaxed text-ink/80">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: accent }} />{i}
      </li>
    ))}
  </ul>
);

const SourceItem = ({ source }) => {
  if (typeof source === "string") return <li className="text-sm text-ink/70">{source}</li>;
  return (
    <li className="text-sm leading-relaxed text-ink/75" data-testid="source-item">
      {source.url ? (
        <a href={source.url} target="_blank" rel="noreferrer noopener"
          className="inline-flex items-center gap-1.5 font-medium text-sage-deep underline decoration-sage/40 underline-offset-2 hover:text-sage"
          data-testid="source-link">
          {source.title} <ExternalLink size={12} className="shrink-0" />
        </a>
      ) : (
        <span className="font-medium text-charcoal">{source.title}</span>
      )}
      <span className="text-ink/55">{source.org ? ` — ${source.org}` : ""}{source.date ? ` (${source.date})` : ""}</span>
    </li>
  );
};

export default function Article() {
  const { slug } = useParams();
  const r = resourceBySlug(slug);
  if (!r) return <Navigate to="/resources" replace />;
  const cat = categoryBySlug(r.category);
  const a = cat?.accent || { tint: "#e9f0e6", deep: "#3E5A44" };
  const related = relatedResources(r, 3);
  const isResearch = r.type === "learning";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: r.title,
    description: r.dek,
    articleSection: cat?.title,
    keywords: (r.tags || []).join(", "),
    image: cat?.image,
    author: { "@type": "Organization", name: "HEARD" },
    publisher: { "@type": "Organization", name: "HEARD" },
  };

  return (
    <HubPage testid={`article-page-${slug}`}>
      <Seo title={`${r.title} — ${typeLabel(r.type)} | HEARD`} description={r.dek} jsonLd={jsonLd} />

      {/* Accent hero */}
      <Band className="relative overflow-hidden border-b border-line/60">
        <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, ${a.tint} 0%, transparent 60%)` }} aria-hidden="true" />
        <Container className="relative pt-14 pb-10 sm:pt-20">
          <div className="mx-auto max-w-3xl">
            <Link to={`/resources/${r.category}`} className="inline-flex items-center gap-1.5 text-sm hover:opacity-70" style={{ color: a.deep }} data-testid="back-to-category">
              <ArrowLeft size={15} /> {cat?.title}
            </Link>
            {cat?.image && (
              <HeroMedia src={cat.image} alt={`${cat?.title} — editorial photograph`} eager className="mt-6 aspect-[16/8] sm:aspect-[16/7]" />
            )}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <TypeChip typeKey={r.type} />
              <span className="text-xs uppercase tracking-[0.14em] text-taupe">{r.readTime} read</span>
            </div>
            <h1 className="serif-display mt-5 text-[2.1rem] sm:text-[2.9rem] font-normal leading-[1.1] text-charcoal">{r.title}</h1>
            <p className="mt-4 text-lg leading-relaxed text-ink/70">{r.dek}</p>
          </div>
        </Container>
      </Band>

      {/* Body */}
      <Container className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl">
          {needsDisclaimer(r.category) && <div className="mb-2"><Disclaimer /></div>}

          {r.whatKnow && (
            <Prose heading={isResearch ? "What's established" : "What we know"} accent={a.deep}><p>{r.whatKnow}</p></Prose>
          )}
          {r.stillLearning && (
            <Prose heading={isResearch ? "What's still being studied" : "What we're still learning"} accent={a.deep}>
              <p>{r.stillLearning}</p>
            </Prose>
          )}
          {r.everyday?.length > 0 && (
            <Prose heading="What this may mean in everyday life" accent={a.deep}><Bullets items={r.everyday} accent={a.deep} /></Prose>
          )}
          {r.worthAsking?.length > 0 && (
            <Prose heading="Questions worth asking" accent={a.deep}><Bullets items={r.worthAsking} accent={a.deep} /></Prose>
          )}

          {r.sources?.length > 0 && (
            <section className="mt-12 rounded-2xl border border-line bg-cream-deep/40 p-6" data-testid="article-sources">
              <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-sage-deep">Sources</h2>
              <ul className="mt-3 space-y-2">
                {r.sources.map((s, i) => <SourceItem key={i} source={s} />)}
              </ul>
            </section>
          )}

          {r.tags?.length > 0 && (
            <div className="mt-8" data-testid="article-tags">
              <span className="text-xs uppercase tracking-[0.14em] text-taupe">Explore related topics</span>
              <div className="mt-3 flex flex-wrap gap-2">
                {r.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/explore?q=${encodeURIComponent(t)}`}
                    onClick={() => track("search_used", t)}
                    data-testid={`article-tag-${t.replace(/[^a-z0-9]+/gi, "-")}`}
                    className="rounded-full border border-line bg-white/70 px-3 py-1.5 text-xs text-sage-deep transition-colors hover:border-sage hover:bg-white"
                  >
                    #{t}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </Container>

      {/* Related */}
      {related.length > 0 && (
        <Band tone="white" className="border-y border-line/60 py-14 sm:py-16">
          <Container>
            <section data-testid="related-resources">
              <h2 className="serif-display text-2xl text-charcoal">Keep reading</h2>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((rr) => <ResourceCard key={rr.slug} resource={rr} />)}
              </div>
            </section>
          </Container>
        </Band>
      )}

      <Container className="py-14 sm:py-16">
        <div className="mx-auto max-w-3xl">
          <NewsletterSignup source="article" />
          <div className="mt-8"><BetaCTA /></div>
          <div className="mt-8"><CommunityCTA compact /></div>
        </div>
      </Container>
    </HubPage>
  );
}
